import React, { useEffect, useState } from 'react';
import {
  X,
  BookOpen,
  CheckCircle,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchWebsiteCourses, type WebsiteCourse } from '../lib/websiteCourses';

const CurrentCourses = () => {
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);
  const [cardWidth, setCardWidth] = useState(296);
  // Im Admin gepflegte Kursdaten; null = Fallback auf eingebaute Inhalte
  const [dbCourses, setDbCourses] = useState<WebsiteCourse[] | null>(null);

  useEffect(() => {
    let active = true;
    fetchWebsiteCourses().then(data => {
      if (active && data) setDbCourses(data);
    });
    return () => { active = false; };
  }, []);

  const gap = 16;
  const step = cardWidth + gap;

  useEffect(() => {
    if (searchParams.get('scroll') === 'kurse') {
      setTimeout(() => {
        document.getElementById('current-courses')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 250);
    }
  }, [searchParams]);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w >= 1280) { setVisibleCount(3); setCardWidth(296); }
      else if (w >= 768) { setVisibleCount(2); setCardWidth(296); }
      else { setVisibleCount(1); setCardWidth(Math.min(w - 32, 340)); }
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const fallbackCards = [
    { level: "Level 1", title: "Buchstabenkurs", startDate: "Sobald Gruppe voll ist", imageUrl: "/buchstaben.png" },
    { level: "Level 2", title: "Grundlagenkurs", startDate: "Sobald Gruppe voll ist", imageUrl: "/MB1.png" },
    { level: "Level 3", title: "Aufbaukurs", startDate: "Sobald Gruppe voll ist", imageUrl: "/Madina 2.png" },
    { level: "Level 4", title: "Fortgeschrittenenkurs", startDate: "Sobald Gruppe voll ist", imageUrl: "/madina3.png" },
    { level: "Level 5", title: "Expertenkurs", startDate: "Sobald Gruppe voll ist", imageUrl: "/arabya4.png" },
    { level: "Level 6", title: "Meisterstufe", startDate: "Sobald Gruppe voll ist", imageUrl: "/Meisterstufe.png" },
  ];

  // Karten: DB-Daten bevorzugen, fehlende Felder aus dem Fallback ergänzen
  const currentCourses = dbCourses
    ? dbCourses.map(c => {
        const fb = fallbackCards.find(f => f.level === c.level);
        return {
          level: c.level,
          title: c.title || fb?.title || c.level,
          startDate: c.start_label || fb?.startDate || 'Sobald Gruppe voll ist',
          imageUrl: c.image_url || fb?.imageUrl || '',
        };
      })
    : fallbackCards;

  const maxIndex = Math.max(currentCourses.length - visibleCount, 0);

  useEffect(() => {
    if (currentIndex > maxIndex) setCurrentIndex(0);
  }, [currentIndex, maxIndex]);

  const go = (dir: number) => {
    setCurrentIndex(prev => {
      const next = prev + dir;
      if (next < 0) return maxIndex;
      if (next > maxIndex) return 0;
      return next;
    });
  };

  const getDetailedCourseInfo = (level: string) => {
    // Detail-Daten aus dem Admin-Dashboard, falls geladen
    const db = dbCourses?.find(c => c.level === level);
    if (db) {
      return {
        level: db.level,
        title: db.title,
        description: db.description,
        duration: db.duration,
        sessions: db.sessions,
        books: db.books ?? [],
        skills: db.skills ?? [],
      };
    }
    const regularCourses = [
      {
        level: "Level 1", title: "Buchstabenkurs",
        description: "Lerne das arabische Alphabet und die grundlegenden Tajweed-Regeln von Grund auf – perfekt für absolute Anfänger.",
        duration: "ca. 20 Wochen", sessions: "ca. 50 Live-Sessions",
        books: ["Eigenes Skript für Lesen und Schreiben", "Tuhfatul Atfal für Tajweed", "Prüfung und Zertifikat"],
        skills: ["Alle Buchstaben lesen und schreiben", "Lese- und Schreibregeln", "Grundlegende Tajweed-Regeln", "Korrekte Aussprache"],
      },
      {
        level: "Level 2", title: "Grundlagenkurs",
        description: "Grundlagen der Sprache und wichtige Grammatik – der perfekte Einstieg nach dem Buchstabenkurs.",
        duration: "ca. 35 Wochen", sessions: "ca. 70 Live-Sessions",
        books: ["Madina Buch 1", "Arabya Bayna Yadayk 1"],
        skills: ["Wichtige Grammatikregeln", "Grundwortschatz aufbauen", "Einfache Gespräche führen", "Leichte Texte verstehen"],
      },
      {
        level: "Level 3", title: "Aufbaukurs",
        description: "Vertiefe deine Sprachkenntnisse und erweitere Schritt für Schritt dein Verständnis.",
        duration: "ca. 35 Wochen", sessions: "ca. 70 Live-Sessions",
        books: ["Madina Buch 2", "Arabya Bayna Yadayk 2"],
        skills: ["Weiterführende Grammatikregeln", "Schwierigere Texte verstehen", "Erweiterten Wortschatz anwenden"],
      },
      {
        level: "Level 4", title: "Fortgeschrittenenkurs",
        description: "Für Lernende, die bereits eine gute Grundlage haben und weiter in die Sprache eintauchen möchten.",
        duration: "ca. 35 Wochen", sessions: "ca. 70 Live-Sessions",
        books: ["Madina Buch 3", "Arabya Bayna Yadayk 3"],
        skills: ["Wichtige fortgeschrittene Grammatik", "Texte analysieren", "Längere Inhalte sicher verstehen"],
      },
      {
        level: "Level 5", title: "Expertenkurs",
        description: "Ein höheres Niveau für alle, die sich intensiv und systematisch weiterentwickeln möchten.",
        duration: "ca. 35 Wochen", sessions: "ca. 70 Live-Sessions",
        books: ["Al-Ajrumiyyah", "Arabya Bayna Yadayk 4"],
        skills: ["Tiefes Grammatikverständnis", "Eigenständiges Arbeiten und Verstehen"],
      },
      {
        level: "Level 6", title: "Meisterstufe",
        description: "Für alle, die langfristig selbstständig mit arabischen Originaltexten arbeiten möchten.",
        duration: "ca. 50 Wochen", sessions: "ca. 100 Live-Sessions",
        books: ["Originaltexte auf Arabisch"],
        skills: ["Selbstständig lesen", "Originaltexte besser verstehen"],
      },
    ];
    return regularCourses.find(c => c.level === level);
  };

  const getCurrentCourseInfo = (level: string) => currentCourses.find(c => c.level === level);

  const getLevelParam = (level: string) => {
    const map: Record<string, string> = {
      'Level 1': 'complete-beginner', 'Level 2': 'alphabet-basic',
      'Level 3': 'basic-words', 'Level 4': 'intermediate',
      'Level 5': 'advanced', 'Level 6': 'advanced',
    };
    return map[level] ?? 'unsure';
  };

  const CourseCard = ({ course }: { course: typeof currentCourses[0] }) => {
    const [imgError, setImgError] = useState(false);
    return (
      <button
        type="button"
        className="group text-left rounded-2xl border border-gray-200 bg-white overflow-hidden hover:border-[#970000]/50 hover:shadow-[0_8px_30px_rgba(151,0,0,0.10)] transition-all duration-200 shrink-0"
        style={{ width: `${cardWidth}px` }}
        onClick={() => setSelectedCourse(course.level)}
        aria-label={`${course.title} – Details öffnen`}
      >
        {/* Image area */}
        <div className="bg-white w-full flex items-center justify-center h-48 px-6 pt-5 pb-3">
          {imgError ? (
            <div className="h-full w-full flex items-center justify-center rounded-t-2xl bg-gray-50">
              <BookOpen className="h-12 w-12 text-gray-200" />
            </div>
          ) : (
            <img
              src={course.imageUrl}
              alt={course.title}
              className="h-full w-full object-contain rounded-t-2xl transition-transform duration-500 group-hover:scale-[1.03]"
              loading="lazy"
              onError={() => setImgError(true)}
            />
          )}
        </div>

        {/* Card body */}
        <div className="px-5 pt-4 pb-5">
          <h3 className="text-[16px] font-bold text-gray-900 mb-1">{course.title}</h3>
          <p className="text-[11px] font-semibold tracking-[0.12em] uppercase text-[#970000] mb-4">
            {course.level}
          </p>
          <div className="flex items-center gap-1.5 text-[13px] font-bold text-[#970000]">
            Kursdetails
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
          </div>
        </div>
      </button>
    );
  };

  return (
    <section id="current-courses" className="py-16 md:py-24 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-10">
          <div className="max-w-xl">
            <p className="text-xs font-semibold tracking-[0.12em] uppercase text-[#970000] mb-3">
              Aktuelle Kurse
            </p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-[-0.04em] leading-[1.1] text-gray-900">
              Arabischkurse online –{' '}
              <span className="oblique font-extrabold text-[#970000]">finde deinen Einstieg</span>
            </h2>
            <p className="mt-3 md:mt-4 text-[15px] md:text-[17px] leading-relaxed text-gray-500">
              Vom arabischen Alphabet bis zum selbstständigen Lesen arabischer Texte – 6 aufbauende Kurse.
            </p>
          </div>
        </div>

        {/* Slider */}
        <div className="overflow-hidden">
          <div
            className="flex transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
            style={{ transform: `translateX(-${currentIndex * step}px)`, gap: `${gap}px` }}
          >
            {currentCourses.map((course, i) => (
              <CourseCard key={i} course={course} />
            ))}
          </div>
        </div>

        {/* Dots + arrows */}
        <div className="flex items-center justify-center gap-4 mt-7">
          <button
            onClick={() => go(-1)}
            className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 hover:border-gray-300 transition-all"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1.5">
            {Array.from({ length: maxIndex + 1 }).map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentIndex(i)}
                className={`h-1 rounded-full transition-all duration-300 ${
                  i === currentIndex ? 'w-6 bg-[#970000]' : 'w-3 bg-gray-200'
                }`}
              />
            ))}
          </div>
          <button
            onClick={() => go(1)}
            className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50 hover:border-gray-300 transition-all"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Modal */}
      <AnimatePresence>
      {selectedCourse && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-50 overflow-y-auto bg-black/40 backdrop-blur-[2px] p-3 sm:p-4 md:p-6"
        >
          <div className="flex min-h-full items-start justify-center py-4 sm:py-8">
            <motion.div
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.97 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="w-full max-w-5xl overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_24px_80px_rgba(0,0,0,0.15)]"
            >
              {(() => {
                const detailedInfo = getDetailedCourseInfo(selectedCourse);
                const currentInfo = getCurrentCourseInfo(selectedCourse);
                if (!detailedInfo || !currentInfo) return null;

                return (
                  <>
                    <div className="relative border-b border-gray-100 bg-white px-4 py-5 sm:px-6 sm:py-6 md:px-7">
                      <button
                        onClick={() => setSelectedCourse(null)}
                        aria-label="Kursdetails schließen"
                        className="absolute right-4 top-4 sm:right-5 sm:top-5 w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <div className="pr-12">
                        <div className="flex flex-wrap items-center gap-2 mb-3">
                          <span className="rounded-full bg-[#970000]/[0.08] px-3 py-1 text-xs font-semibold text-[#970000]">
                            {selectedCourse}
                          </span>
                          <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-500">
                            Start: {currentInfo.startDate}
                          </span>
                        </div>
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-[-0.03em] text-gray-900">
                          {detailedInfo.title}
                        </h2>
                        <p className="mt-2 text-[13px] sm:text-[14px] leading-relaxed text-gray-500 max-w-2xl">
                          {detailedInfo.description}
                        </p>
                      </div>
                    </div>

                    <div className="px-4 py-5 sm:px-6 sm:py-6 md:px-7">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-6">
                        {[
                          { label: 'Start', value: currentInfo.startDate },
                          { label: 'Dauer', value: detailedInfo.duration },
                          { label: 'Live-Sessions', value: detailedInfo.sessions },
                          { label: 'Gruppe', value: '4–8 Schüler' },
                        ].map(({ label, value }) => (
                          <div key={label} className="rounded-xl border border-gray-100 bg-gray-50/60 p-3.5">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#970000] mb-1">{label}</p>
                            <p className="text-[13px] text-gray-700">{value}</p>
                          </div>
                        ))}
                      </div>

                      <div className="grid lg:grid-cols-2 gap-4 mb-6">
                        <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-5">
                          <h4 className="flex items-center gap-2 text-[14px] font-semibold text-gray-900 mb-3">
                            <CheckCircle className="h-4 w-4 text-[#970000]" />
                            Das lernst du
                          </h4>
                          <div className="space-y-2">
                            {detailedInfo.skills.map((skill, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <CheckCircle className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" />
                                <span className="text-[13px] text-gray-600">{skill}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-5">
                          <h4 className="flex items-center gap-2 text-[14px] font-semibold text-gray-900 mb-3">
                            <BookOpen className="h-4 w-4 text-[#970000]" />
                            Lehrmaterialien
                          </h4>
                          <div className="space-y-2">
                            {detailedInfo.books.map((book, idx) => (
                              <div key={idx} className="flex items-start gap-2">
                                <div className="h-1.5 w-1.5 rounded-full bg-[#970000] mt-1.5 shrink-0" />
                                <span className="text-[13px] text-gray-600">{book}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100">
                        <Link
                          to={`/anmelden?course=${encodeURIComponent(selectedCourse)}&level=${getLevelParam(selectedCourse)}`}
                          onClick={() => setSelectedCourse(null)}
                          className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 font-semibold text-[14px] text-white transition-filter"
                          style={{ background: 'linear-gradient(135deg,rgba(255,255,255,.15),transparent 50%,rgba(0,0,0,.15)), #b30000' }}
                        >
                          Jetzt anmelden
                        </Link>
                        <a
                          href="#contact"
                          onClick={() => setSelectedCourse(null)}
                          className="inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 px-6 py-3 font-semibold text-[14px] text-gray-700 transition-colors hover:bg-gray-50"
                        >
                          Kostenlose Beratung
                        </a>
                      </div>
                    </div>
                  </>
                );
              })()}
            </motion.div>
          </div>
        </motion.div>
      )}
      </AnimatePresence>
    </section>
  );
};

export default CurrentCourses;
